import React, {PureComponent} from 'react';
import {Switch, Route} from 'react-router-dom';
import MediaQuery from 'react-responsive';
import FileSaver from 'filesaver.js';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import Screen from '../../components/Screen';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Notification from '../../components/Notification';
import Content from '../../components/Content';
import Tabs from '../../components/Tabs';
import Button from '../../components/Button';
import Menu from '../../components/Menu';
import Icon from '../../components/Icon';
import ExportFormDataModal from '../../components/ExportFormDataModal';
import { T } from '../../components/I18n';

import {MAX_ROWS, MAX_SHEETS, DEFAULT_SHEETS} from './shared';
import SheetSelector from './SheetSelector';
import SheetRowFilter from './SheetRowFilter';
import SheetDashboardTab from './SheetDashboardTab';
import ViewDataTab from './ViewDataTab';
import { CancelController, CancelledException } from '../../lib/cancel';
import FilterInfoMessage from './FilterInfoMessage';

class SheetScreen extends PureComponent {
  state = {
    template: null,
    loading: true,
    failed: false,
    tooManyRows: false,
    rowsBySheetId: {},
    filters: null,
    actionsOpen: false,
    manageSheets: false,
    filterRows: false,
    exportForm: false,
    exportSheets: false,
    pinnedWidgets: [],
    numberOfSheetsLoaded: 0,
    showDeleted: false,
    exportDeleted: false,
  };

  cancelController = new CancelController();

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    // Cancel any in-progress loading.
    this.cancelController.cancel();
  }

  componentDidUpdate(prevProps, prevState) {
    const {match: {params: {sheetIds}}} = this.props;
    const {match: {params: {sheetIds: prevSheetIds}}} = prevProps;
    const {filters} = this.state;
    const {filters: prevFilters} = prevState;

    if (sheetIds !== prevSheetIds || filters !== prevFilters) {
      this.load();
    }
  }

  async load() {
    this.cancelController.cancel(); // Cancel any previous load invocations.
    this.cancelController = new CancelController();
    const cancellable = promise => this.cancelController.cancellable(promise);

    this.setState({loading: true, tooManyRows: false, numberOfSheetsLoaded: 0});

    const {credentials, match: {params: {templateId, sheetIds: sheetIdsAsString}}} = this.props;
    let {filters} = this.state;

    if(filters === null && this.props.location.state && this.props.location.state.filter) {
      filters = JSON.parse(this.props.location.state.filter);

      if(filters != null) {
        if(filters.maxDate) {
          filters.maxDate = new Date(filters.maxDate);
        }
        if(filters.minDate) {
          filters.minDate = new Date(filters.minDate);
        }
      }

      this.setState({filters});
    }

    const sheetIds = sheetIdsAsString.split(',');

    try {
      const template = await cancellable(api.getTemplate(templateId, credentials));
      const catalog = await cancellable(api.getCatalog(credentials));
      const pinnedWidgets = await cancellable(api.getPinnedWidgets(credentials));

      this.setState({
        pinnedWidgets: pinnedWidgets,
        catalog,
        template: {
          ...template,
          contextQuestions: [...template.contextQuestions].sort((a, b) => a.order - b.order),
          questions: [...template.questions].sort((a, b) => a.order - b.order)
        }
      });

      // Load the sheets.
      let unfilteredSheets = [];

      if (sheetIdsAsString === 'all') {
        // Get the last DEFAULT_SHEETS sheets.
        unfilteredSheets = (await cancellable(api.getSheets(templateId, 0, DEFAULT_SHEETS, "dates.lastModifiedDate,desc", credentials))).content;
      }
      else {
        if (sheetIds.length > MAX_SHEETS) {
          this.setState({tooManyRows: true, template, sheets: [], loading: false});
          return;
        }

        for (let sheetId of sheetIds) {
          unfilteredSheets.push(await cancellable(api.getSheet(sheetId, credentials)));
        }
      }

      const sheets = unfilteredSheets
        .filter(sheet => sheet.templateId === templateId)
        .sort((a, b) => b.dates.lastModified - a.dates.lastModified);
      
      this.setState({sheets});

      // Now for each sheet, load all of it's rows. We will do this serially to prevent excessive requests on the API.
      let rowsBySheetId = {};
      let rowCount = 0;

      for (let sheet of sheets) {
        const rows = await cancellable(api.getAllSheetRows(sheet, filters, credentials, this.cancelController));
        rowsBySheetId[sheet.id] = rows;
        rowCount += rows.length;
        sheet.rowCount = rows.length;

        if (rowCount > MAX_ROWS) {
          this.setState({tooManyRows: true, template, sheets, loading: false});
          return;
        }

        this.setState(prevState => ({numberOfSheetsLoaded: prevState.numberOfSheetsLoaded + 1}));
      }

      this.setState({rowsBySheetId, loading: false});
    }
    catch(e) {
      if (!(e instanceof CancelledException)) {
        console.error(e);
        this.setState({failed: true, loading: false});
      }
    }
  }

  handleRowChanged = (sheet, row) => {
    // Update the state for the changed row.
    this.setState(prevState => ({
      rowsBySheetId: {
        ...prevState.rowsBySheetId,
        [sheet.id]: prevState.rowsBySheetId[sheet.id].map(r => r.id === row.id ? row : r)
      }
    }));
  }

  handleRowDeleted = (sheet, row) => {
    // Remove the row for that sheet.
    this.setState(prevState => ({
      rowsBySheetId: {
        ...prevState.rowsBySheetId,
        [sheet.id]: prevState.rowsBySheetId[sheet.id].filter(r => r.id !== row.id)
      }
    }));
  }

  handleAnswersChanged = (sheet, row, question, answers) => {
    let {sheets, rowsBySheetId} = this.state;

    // Update the sheets state if the question is a context question.
    if (question.context) {
      sheets = sheets.map(s => {
        if (s.id === sheet.id) {
          return {
            ...s,
            contextAnswers: s.contextAnswers.filter(answer => answer.questionId !== question.id).concat(answers)
          };
        }

        return s;
      });
    }

    // Update the rowsBySheetId state if the question is not a context question.
    if (!question.context) {
      rowsBySheetId = {
        ...rowsBySheetId,
        [sheet.id]: rowsBySheetId[sheet.id].map(r => {
          if (r.id === row.id) {
            return {
              ...r,
              answers: r.answers.filter(answer => answer.questionId !== question.id).concat(answers)
            };
          }

          return r;
        })
      };
    }

    // Commit the state changes.
    this.setState({sheets, rowsBySheetId});
  }

  handleExportSheets = async () => {
    const {credentials} = this.props;
    const {sheets, template, exportDeleted} = this.state;
    const now = new Date();
    const {match: {params: {sheetIds}}} = this.props;
    let sheetIdsAsList = sheetIds.split(',');

    if (sheetIds === 'all') {
      sheetIdsAsList = [];

      for (let i = 0; i < this.countSheets(); i++) {
        sheetIdsAsList[i] = sheets[i].id;
      }
    }

    this.setState({exportSheets: true});

    try {
      const content = await api.exportMultiSheetData(sheetIdsAsList, exportDeleted, credentials);
      FileSaver.saveAs(content, `${template.name}-${now.getTime()}.xlsx`);
    }
    catch (e) {
      if(e.status === 400) {
        NotificationManager.error('Too much data to export. Reduce your date range and try again.');
      }
      else {
        NotificationManager.error('Unable to export data. Please try again.');
      }
    }
    finally {
      this.setState({exportSheets: false});
    }
  }

  handleApplyFilters = filters => {
    this.setState({filterRows: false, filters});
  }

  updateTemplate = (template, onComplete = () => null) => {
    this.setState({template}, onComplete);
  }

  toggleActions = () => {
    this.setState(prevState => ({actionsOpen: !prevState.actionsOpen}));
  }

  toggleSelectSheets = () => {
    this.setState(prevState => ({manageSheets: !prevState.manageSheets}));
  }

  toggleFilters = () => {
    this.setState(prevState => ({filterRows: !prevState.filterRows}));
  }

  toggleFormExport = () => {
    this.setState(prevState => ({exportForm: !prevState.exportForm}));
  }

  toggleShowDeleted =() => {
    this.setState(prevState => ({showDeleted: !prevState.showDeleted}))
  }

  toggleExportDeleted = () => {
    this.setState(prevState => ({exportDeleted: !prevState.exportDeleted}))
  }

  countSheets = () => {
    const {sheets} = this.state;
    const {match: {params: {sheetIds}}} = this.props;

    if (!sheets) {
      if (sheetIds === 'all') {
        return 0;
      }

      return sheetIds.split(',').length;
    }
    
    return sheets.length;
  }

  renderDashboard = () => {
    const {sheets, template, rowsBySheetId, filters, catalog, pinnedWidgets, showDeleted} = this.state;

    return (
      <SheetDashboardTab
        sheets={sheets}
        template={template}
        rows={rowsBySheetId}
        filters={filters}
        catalog={catalog}
        onTemplateChanged={this.updateTemplate}
        onApplyFilters={this.handleApplyFilters}
        pinnedWidgets={pinnedWidgets}
        showDeleted={showDeleted}
      />
    );
  }

  renderViewDataTab = () => {
    const {sheets, template, rowsBySheetId, filters, catalog, showDeleted} = this.state;

    return (
      <ViewDataTab
        sheets={sheets}
        template={template}
        rows={rowsBySheetId}
        filters={filters}
        catalog={catalog}
        onRowChange={this.handleRowChanged}
        onRowDeleted={this.handleRowDeleted}
        onAnswersChange={this.handleAnswersChanged}
        onApplyFilters={this.handleApplyFilters}
        showDeleted={showDeleted}
      />
    );
  }

  renderContainer = isSmallScreen => {
    const {showDeleted, loading, numberOfSheetsLoaded, failed, tooManyRows, template, filters, catalog, actionsOpen, manageSheets, filterRows, exportForm, exportSheets, exportDeleted} = this.state;
    const numberOfSelectedSheets = this.countSheets();

    if (loading) {
      return (
        <Screen activeLink="Dashboard">
          <Loading>
            {numberOfSelectedSheets === 0 
              ? <>Loading...</>
              : <>Loaded {numberOfSheetsLoaded} of {numberOfSelectedSheets} sheets...</>
            }
          </Loading>
        </Screen>
      );
    }

    if (failed) {
      return (
        <Screen activeLink="Dashboard">
          <Error message="Unable to load sheet" />
        </Screen>
      );
    }

    const {location, user, match: {params: {templateId, sheetIds}}} = this.props;
    const dashboardUrl = `/sheet/${templateId}/${sheetIds}`;
    const dataUrl = `/sheet/${templateId}/${sheetIds}/data`;
    const sheetIdsAsList = sheetIds.split(',');

    return (
      <Screen
        title={<T noTranslate>{template.name}</T>}
        windowTitle={template.name}
        activeLink="Dashboard"
        actions={!isSmallScreen ? [
          <Button id="dashboard-actions" onClick={this.toggleActions}>
            Options <Icon name="chevron-down" />
          </Button>
        ] : [
          <Button onClick={this.toggleFilters}>Filter Data</Button>,
          [...template.contextQuestions, ...template.questions].some(question => question.deleted) && <Button onClick={this.toggleShowDeleted}>
            {showDeleted ? "Hide deleted question data" : "Show deleted question data"}</Button>,
          <Button onClick={this.toggleSelectSheets}><T isolate>Select Sheets</T> ({numberOfSelectedSheets})</Button>
        ]}
        tabs={!tooManyRows && (
          <Tabs>
            <Tabs.Tab to={dashboardUrl} active={location.pathname === dashboardUrl}>Dashboard</Tabs.Tab>
            <Tabs.Tab to={dataUrl} active={location.pathname.indexOf(dataUrl) === 0}>View Data</Tabs.Tab>
          </Tabs>
        )}>
        {tooManyRows ? (
          <Content>
            <Notification error>
              There is too much data to display. The maximum amount of data that can be displayed is {MAX_ROWS.toLocaleString()} sheet responses or {MAX_SHEETS.toLocaleString()} sheets.
              Please select less sheets with less responses or apply filters to continue.
            </Notification>
          </Content>
        ) : (
          <>
            <FilterInfoMessage
              numberOfSheets={numberOfSelectedSheets}
              sheetIds={sheetIdsAsList}
              filters={filters}
            />
            <Switch>
              <Route path="/sheet/:templateId/:sheetIds/data" component={this.renderViewDataTab} />
              <Route path="/sheet/:templateId/:sheetIds" component={this.renderDashboard} />
            </Switch>
          </>
        )}
        <Menu
          open={actionsOpen} 
          position="bottom-end"  
          offsetY={5} 
          relativeTo="dashboard-actions" 
          onClose={this.toggleActions}>
          <Menu.Section>
            <Menu.Text>Filter Options</Menu.Text>
            <Menu.Divider />
            <Menu.Option onClick={this.toggleFilters}>Filter data</Menu.Option>
            {[...template.contextQuestions, ...template.questions].some(question => question.deleted) && (
              <Menu.Option keepOpen onClick={this.toggleShowDeleted}> {showDeleted ? "Hide deleted question data" : "Show deleted question data"}</Menu.Option>
            )}
            <Menu.Option onClick={this.toggleSelectSheets}><T isolate>Select sheets</T> ({numberOfSelectedSheets})</Menu.Option>
          </Menu.Section>
          {user.role !== "USER" &&
          <Menu.Section>
            <Menu.Text>Export Options</Menu.Text>
            <Menu.Divider />
            <Menu.Option disabled={exportSheets} onClick={this.handleExportSheets}>
              {exportSheets ? 'Exporting...' : <T isolate>Export <T ignore>{numberOfSelectedSheets}</T> sheet(s)</T>}
            </Menu.Option>
            {[...template.contextQuestions, ...template.questions].some(question => question.deleted) && (
              <Menu.Option keepOpen active={exportDeleted} onClick={this.toggleExportDeleted}>
                Include deleted question data {exportDeleted ? "✓ " : "✖ "}
              </Menu.Option>
            )}
            <Menu.Option onClick={this.toggleFormExport}>Export all form data</Menu.Option>
          </Menu.Section>}
        </Menu>
        <SheetSelector
          open={manageSheets}
          onClose={this.toggleSelectSheets}
          template={template}
          selectedIds={sheetIdsAsList}
        />
        <SheetRowFilter
          open={filterRows}
          onClose={this.toggleFilters}
          template={template}
          catalog={catalog}
          filters={filters}
          onApplyFilters={this.handleApplyFilters}
        />
        <ExportFormDataModal
          form={exportForm && template}
          onClose={this.toggleFormExport}
          includeDeletedAnswers={exportDeleted}
        />
      </Screen>
    );
  }

  render() {
    return <MediaQuery maxWidth={800} children={this.renderContainer} />;
  }
}

export default withUser(SheetScreen);
